import { Controller } from "@hotwired/stimulus";
import { endSkeletonWait } from "../skeleton_wait_support";
import {
  connectImmediateSortSync,
  disconnectImmediateSortSync,
  markTableFrameLoaded,
  matchesTurboFrameFetch,
  showTableSkeleton,
  turboFrameHeader,
} from "../table_frame_support";

const TAB_NAVIGATION_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"]);
const TAB_ACTIVATION_KEYS = new Set([" ", "Enter"]);

export default class extends Controller {
  static targets = [
    "frame",
    "skeletonTemplate",
    "tabBadgeSkeleton",
    "form",
    "tab",
    "tabBadge",
    "scopeField",
    "statusDropdown",
    "statusDropdownItem",
    "searchStatus",
  ];
  static values = {
    activeTabClass: String,
    inactiveTabClass: String,
    tableFrameId: {
      type: String,
      default: "educator-home-page-course-monitoring-table-frame",
    },
    cardFrameId: {
      type: String,
      default: "educator-home-page-course-monitoring",
    },
  };

  connect() {
    this.syncCourseMonitoringScopeField();
    this.syncTabLinkScope();
    this.syncStatusTabFromUrl();
    this.applySelectedTabRovingTabindex();

    this.boundSyncStatusTabFromUrl = this.syncStatusTabFromUrl.bind(this);
    document.addEventListener("turbo:load", this.boundSyncStatusTabFromUrl);
    window.addEventListener("popstate", this.boundSyncStatusTabFromUrl);

    connectImmediateSortSync(this);
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.boundSyncStatusTabFromUrl);
    window.removeEventListener("popstate", this.boundSyncStatusTabFromUrl);
    disconnectImmediateSortSync(this);
  }

  syncTabLinkScope() {
    if (!this.hasFormTarget) return;

    const myCourses = this.formTarget.querySelector(
      'input[name="scope"]',
    )?.value;
    const search =
      this.formTarget.querySelector('input[name="search"]')?.value ?? "";

    const syncLink = (link) => {
      if (!link.href) return;

      const url = new URL(link.href, window.location.origin);
      if (myCourses != null) {
        url.searchParams.set("scope", myCourses);
      }
      url.searchParams.set("page", "1");
      if (search.trim() === "") {
        url.searchParams.delete("search");
      } else {
        url.searchParams.set("search", search);
      }
      link.href = url.toString();
    };

    this.tabTargets.forEach(syncLink);
    this.statusDropdownItemTargets.forEach(syncLink);
  }

  tablistKeydown(event) {
    if (!(event.target instanceof Element)) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.tabTargets.includes(tab)) return;

    const { key } = event;
    if (!TAB_NAVIGATION_KEYS.has(key) && !TAB_ACTIVATION_KEYS.has(key)) return;

    if (TAB_ACTIVATION_KEYS.has(key)) {
      if (key === " ") {
        event.preventDefault();
        tab.click();
      }
      return;
    }

    event.preventDefault();
    const currentIndex = this.tabTargets.indexOf(tab);
    if (currentIndex < 0) return;

    const nextIndex = this.nextTabIndexForKey(currentIndex, key);
    if (nextIndex == null) return;

    const nextTab = this.tabTargets[nextIndex];
    this.applyRovingTabindex(nextTab);
    nextTab.focus();
  }

  nextTabIndexForKey(currentIndex, key) {
    const length = this.tabTargets.length;
    if (length === 0) return null;

    switch (key) {
      case "ArrowRight":
        return (currentIndex + 1) % length;
      case "ArrowLeft":
        return (currentIndex - 1 + length) % length;
      case "Home":
        return 0;
      case "End":
        return length - 1;
      default:
        return null;
    }
  }

  applyRovingTabindex(focusedTab) {
    this.tabTargets.forEach((tab) => {
      tab.tabIndex = tab === focusedTab ? 0 : -1;
    });
  }

  applySelectedTabRovingTabindex() {
    if (this.tabTargets.length === 0) return;

    const active = this.activeTabKey() || this.tabTargets[0].dataset.tab;
    this.tabTargets.forEach((tab) => {
      tab.tabIndex = tab.dataset.tab === active ? 0 : -1;
    });
  }

  tabClicked(event) {
    const tab = event.currentTarget.dataset.tab;
    if (!tab) return;

    this.beginStatusTabChange(tab);
  }

  statusDropdownItemClicked(event) {
    const tab = event.currentTarget.dataset.tab;
    if (!tab) return;

    this.beginStatusTabChange(tab);
  }

  beginStatusTabChange(tab) {
    this.pendingSearchRequestId = null;
    this.tabRequestId = (this.tabRequestId || 0) + 1;
    this.pendingTab = tab;
    this.pendingRequestId = this.tabRequestId;
    this.previousTab = this.previousTab ?? this.activeTabKey();
    this.tabRequestFailed = false;
    this.updateTabStates(tab);
    this.updateStatusDropdownLabel(tab);
    this.updateHiddenStatusTab(tab);
    this.resetPageField();
    this.syncTabLinkScope();
    this.showLoadingSkeleton();
  }

  beforeTableFetch(event) {
    if (
      !matchesTurboFrameFetch(event, [
        this.tableFrameIdValue,
        this.cardFrameIdValue,
      ])
    )
      return;

    this.activeFetchRequestId = this.pendingRequestId ?? this.tabRequestId ?? 0;
    this.showLoadingSkeleton();
  }

  beforeTableFetchResponse(event) {
    const headers = event.detail?.fetchOptions?.headers;
    if (
      !matchesTurboFrameFetch({ detail: { fetchOptions: { headers } } }, [
        this.tableFrameIdValue,
        this.cardFrameIdValue,
      ])
    )
      return;

    const response = event.detail?.fetchResponse?.response;
    if (response && !response.ok) {
      this.tabRequestFailed = true;
      if (
        turboFrameHeader(headers) === this.cardFrameIdValue &&
        this.hasFrameTarget
      ) {
        markTableFrameLoaded(this);
        endSkeletonWait(this.frameTarget);
      }
    }
  }

  tableLoaded() {
    if (!this.hasFrameTarget) return;

    markTableFrameLoaded(this);

    const loadId = this.activeFetchRequestId;
    const supersededByPendingClick =
      this.pendingRequestId != null &&
      loadId != null &&
      loadId !== this.pendingRequestId;
    const olderThanCommitted =
      this.lastCommittedRequestId != null &&
      loadId != null &&
      loadId < this.lastCommittedRequestId;

    if (supersededByPendingClick || olderThanCommitted) {
      this.updateTabPanelLabelledBy(this.pendingTab || this.activeTabKey());
      return;
    }

    if (this.tabRequestFailed) {
      this.revertPendingTab();
      this.announceSearchResults(loadId);
      return;
    }

    if (this.pendingTab) {
      this.updateTabStates(this.pendingTab);
      this.updateStatusDropdownLabel(this.pendingTab);
      this.clearPendingTab();
    } else {
      this.syncStatusTabFromUrl();
      this.syncTabPanelLabelledByFromFrame();
    }

    if (loadId != null) this.lastCommittedRequestId = loadId;
    this.syncTabBadgesFromFrame();
    this.syncStatusDropdownLabelsFromTabs();
    this.syncTabLinkScope();
    this.announceSearchResults(loadId);
    this.pendingRequestId = null;
    this.activeFetchRequestId = null;
  }

  syncTabBadgesFromFrame() {
    const payload = this.frameTarget.querySelector(
      '[data-educator-home-page--course-monitoring-table-frame-target="tabCountsPayload"]',
    );
    if (!payload?.dataset?.tabCounts) return;

    let counts;
    try {
      counts = JSON.parse(payload.dataset.tabCounts);
    } catch {
      return;
    }

    this.tabTargets.forEach((tab) => {
      const key = tab.dataset.tab;
      if (!key) return;

      const count = Number(counts[key] ?? 0);
      const badge = tab.querySelector(
        '[data-educator-home-page--course-monitoring-table-frame-target="tabBadge"]',
      );
      if (!badge) return;

      badge.hidden = false;
      badge.removeAttribute("aria-hidden");
      badge.textContent = String(count);
    });
  }

  showLoadingSkeleton() {
    showTableSkeleton(this);
    this.showTabBadgeSkeletons();
  }

  showTabBadgeSkeletons() {
    if (!this.hasTabBadgeSkeletonTarget) return;

    const html = this.tabBadgeSkeletonTarget.innerHTML;
    this.tabBadgeTargets.forEach((badge) => {
      badge.hidden = false;
      badge.setAttribute("aria-hidden", "true");
      badge.innerHTML = html;
    });
  }

  scopeChanged() {
    this.tabRequestId = (this.tabRequestId || 0) + 1;
    this.pendingRequestId = this.tabRequestId;
    this.tabRequestFailed = false;
    this.setFormTurboFrame(this.cardFrameIdValue);
    this.syncCourseMonitoringScopeField();
    this.resetPageField();
    this.showLoadingSkeleton();
  }

  searchChanged() {
    this.tabRequestId = (this.tabRequestId || 0) + 1;
    this.pendingSearchRequestId = this.tabRequestId;
    this.pendingRequestId = this.tabRequestId;
    this.tabRequestFailed = false;
    this.setFormTurboFrame(this.tableFrameIdValue);
    this.resetPageField();
    this.syncTabLinkScope();
  }

  setFormTurboFrame(frameId) {
    if (!this.hasFormTarget) return;

    this.formTarget.dataset.turboFrame = frameId;
  }

  syncCourseMonitoringScopeField() {
    if (!this.hasFormTarget || !this.hasScopeFieldTarget) return;

    const checked = this.formTarget.querySelector(
      'input[name="course_monitoring_scope"]:checked',
    );
    if (!checked) return;

    this.scopeFieldTarget.value = checked.value;
    this.syncTabLinkScope();
  }

  updateTabStates(activeTab) {
    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.tab === activeTab;
      tab.className = isActive
        ? this.activeTabClassValue
        : this.inactiveTabClassValue;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
    });
    this.updateTabPanelLabelledBy(activeTab);
  }

  updateTabPanelLabelledBy(activeTab) {
    if (!this.hasFrameTarget) return;

    const tab = this.tabTargets.find((link) => link.dataset.tab === activeTab);
    const tabId = tab?.id;
    if (tabId) {
      this.frameTarget.setAttribute("aria-labelledby", tabId);
    } else {
      this.frameTarget.removeAttribute("aria-labelledby");
    }
  }

  syncTabPanelLabelledByFromFrame() {
    const payload = this.frameTarget.querySelector(
      '[data-educator-home-page--course-monitoring-table-frame-target="tabCountsPayload"]',
    );
    const statusTab = payload?.dataset?.statusTab;
    if (statusTab) this.updateTabPanelLabelledBy(statusTab);
  }

  announceSearchResults(loadId) {
    if (this.pendingSearchRequestId == null) return;
    if (loadId !== this.pendingSearchRequestId) return;

    this.pendingSearchRequestId = null;
    if (!this.hasSearchStatusTarget) return;

    const payload = this.frameTarget.querySelector(
      '[data-educator-home-page--course-monitoring-table-frame-target="tabCountsPayload"]',
    );
    const searchActive = payload?.dataset?.searchActive === "true";
    if (!searchActive) {
      this.searchStatusTarget.textContent = "";
      return;
    }

    const count = Number(payload?.dataset?.resultCount ?? 0);
    const message = count === 0 ? "No courses found" : `${count} courses found`;
    if (this.searchStatusTarget.textContent === message) return;

    this.searchStatusTarget.textContent = "";
    requestAnimationFrame(() => {
      this.searchStatusTarget.textContent = message;
    });
  }

  activeTabKey() {
    const active = this.tabTargets.find(
      (tab) => tab.getAttribute("aria-selected") === "true",
    );
    return active?.dataset?.tab || null;
  }

  revertPendingTab() {
    if (this.previousTab) {
      this.updateTabStates(this.previousTab);
      this.updateStatusDropdownLabel(this.previousTab);
      this.updateHiddenStatusTab(this.previousTab);
    }
    this.clearPendingTab();
  }

  syncStatusTabFromUrl() {
    const tab = new URL(window.location.href).searchParams.get("status_tab");
    if (!tab || !this.tabTargets.some((link) => link.dataset.tab === tab))
      return;

    this.updateTabStates(tab);
    this.updateStatusDropdownLabel(tab);
    this.updateHiddenStatusTab(tab);
  }

  updateStatusDropdownLabel(tab) {
    if (!this.hasStatusDropdownTarget) return;

    this.statusDropdownItemTargets.forEach((item) => {
      const isActive = item.dataset.tab === tab;
      item.classList.toggle("is-selected", isActive);
      if (isActive) {
        item.setAttribute("aria-current", "true");
      } else {
        item.removeAttribute("aria-current");
      }
    });

    const item = this.statusDropdownItemTargets.find(
      (link) => link.dataset.tab === tab,
    );
    const label = item?.textContent?.trim();
    const triggerLabel = this.statusDropdownTarget.querySelector(".btn-label");
    if (triggerLabel && label) triggerLabel.textContent = label;
  }

  syncStatusDropdownLabelsFromTabs() {
    this.tabTargets.forEach((tab) => {
      const key = tab.dataset.tab;
      if (!key) return;

      const item = this.statusDropdownItemTargets.find(
        (link) => link.dataset.tab === key,
      );
      if (!item) return;

      const baseLabel = item.dataset.statusTabLabel;
      if (!baseLabel) return;

      const badge = tab.querySelector(
        '[data-educator-home-page--course-monitoring-table-frame-target="tabBadge"]',
      );
      const count = badge?.textContent?.trim() ?? "0";
      item.textContent = `${baseLabel} (${count})`;
    });

    const activeTab = this.activeTabKey();
    if (activeTab) this.updateStatusDropdownLabel(activeTab);
  }

  clearPendingTab() {
    this.pendingTab = null;
    this.previousTab = null;
    this.pendingRequestId = null;
    this.activeFetchRequestId = null;
    this.tabRequestFailed = false;
  }

  updateHiddenStatusTab(tab) {
    if (!this.hasFormTarget) return;

    const input = this.formTarget.querySelector('input[name="status_tab"]');
    if (input) input.value = tab;
  }

  resetPageField() {
    if (!this.hasFormTarget) return;

    const input = this.formTarget.querySelector('input[name="page"]');
    if (input) input.value = "1";
  }
}
